import { memo, useMemo } from "react";
import { Layer } from "react-konva";
import type { LayerBookingItem } from "@/api/services/booking.service";
import type { PointType } from "@/api/services/layer.service";
import type { MetablockNodes } from "@/api/services/project.service";
import { useStepRouteNavigation } from "@/components/layout/RouteNavigator/useStepRouteNavigation";
import { useMapStore } from "@/stores/mapStore";
import { useNavigationStore } from "@/stores/navigationStore";
import { AnimatedPoint } from "./Point";
import { RouteTrail } from "./RouteTrail";
import StartEndMarker from "./StartEndMarker";

interface PointsLayerProps {
	colors?: any;
	userDepartmentFieldId?: string | null;
	userFulltimeFieldId?: string | null;
	options?: {
		labelSize: number;
		fontSize: number;
		color: string;
		borderWidth: number;
		wrapText: boolean;
	};
	points?: PointType[];
	nodes?: MetablockNodes;
	bookings?: LayerBookingItem[];
}

const defaultOptions = {
	labelSize: 0.015,
	fontSize: 12,
	color: "#ffffff",
	borderWidth: 0.1,
	wrapText: false,
};

const getBookable = (pluginData) => {
	if (!pluginData) return false;

	let isBookable = false;

	Object.values(pluginData).forEach((obj: any) => {
		const hasProp = Object.keys(obj).includes("bookable");

		if (hasProp) {
			isBookable = obj["bookable"];
		}
	});

	return isBookable;
};

const PointsLayer: React.FC<PointsLayerProps> = ({
	userDepartmentFieldId,
	userFulltimeFieldId,
	colors,
	nodes = {},
	options = defaultOptions,
	bookings = [],
	points = [],
}) => {
	const [width, height] = useMapStore((state) => state.size);
	const layers = useMapStore((state) => state.layers);
	const isRouteActive = useNavigationStore((state) => state.isRouteActive);
	const selected = useNavigationStore((state) => state.selected);
	const route = useNavigationStore((state) => state.route);
	const { route: routePoints } = useStepRouteNavigation();
	const hasRoutePreview = Boolean(selected.from && selected.to);
	const showRoute = (hasRoutePreview || isRouteActive) && routePoints.length > 0;
	const isBookableVisible = useMemo(() => layers["bookable"], [layers]);
	const isNotBookableVisible = useMemo(() => layers["not-bookable"], [layers]);

	const displayPoints = useMemo(() => {
		return points.filter((point) => {
			const isBookable = getBookable(point.plugin_data);

			if (isBookableVisible && isBookable) return true;
			return isNotBookableVisible && !isBookable;
		});
	}, [isBookableVisible, isNotBookableVisible, points]);

	const routeFromId = isRouteActive ? route.from?.id : selected.from?.id;
	const routeToId = isRouteActive ? route.to?.id : selected.to?.id;

	const fromPoint = useMemo(
		() => displayPoints.find((point) => point.id === routeFromId),
		[displayPoints, routeFromId],
	);
	const toPoint = useMemo(
		() => displayPoints.find((point) => point.id === routeToId),
		[displayPoints, routeToId],
	);

	if (width === 0 || height === 0) return null;

	return (
		<Layer>
			{displayPoints.map((point) => (
				<AnimatedPoint
					colors={colors}
					key={point.id}
					id={point.id}
					point={point}
					options={options}
					nodes={nodes}
					bookings={bookings}
					userDepartmentFieldId={userDepartmentFieldId}
					userFulltimeFieldId={userFulltimeFieldId}
				/>
			))}
			{showRoute && <RouteTrail points={routePoints} />}
			{showRoute && fromPoint && (
				<StartEndMarker point={fromPoint} options={options} nodes={nodes} />
			)}
			{showRoute && toPoint && (
				<StartEndMarker point={toPoint} options={options} nodes={nodes} />
			)}
		</Layer>
	);
};

export default memo(PointsLayer);

// PointsLayer.whyDidYouRender = true
